'use client';

import { getContact, updateContact } from '@/api/contacts';
import { getSalesforceContact } from '@/api/salesforce-embed';
import { useSalesforceEmbed } from '@/app/context/salesforce-embed';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { useIntercom } from '../common/IntercomProvider';
import ContactMiniCard from '../common/ContactMiniCard';
import CompanyMiniCard from '../common/CompanyMiniCard';
import Drawer from '../ui/Drawer';
import { Skeleton } from '../ui/Skeleton';
import Tabs, { TabsList, TabsTrigger } from '../ui/Tabs';
import {
  Building,
  Check,
  Copy,
  Globe,
  Linkedin,
  ListCheck,
  Mail,
  Megaphone,
  PhoneCall,
  Radio,
  User,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import ContactTargetList from '../contact/tabs/ContactTargetList';
import QualificationPill from '../common/QualificationPill';
import Icon from '../ui/Icon';
import InfoTable, { type InfoTableItem } from '../common/InfoTable';
import { cn, formatDate, objectPathSet } from '@/app/lib/utils';
import { CustomScroll } from '../ui/CustomScroll';
import SignalDetails from './SignalDetails';
import type { SignalContactSummary } from '@/types/signals';
import type { Contact } from '@/types/contact';
import styles from './ContactDetailsDrawer.module.css';
import { useTranslations } from 'next-intl';
import { getAttributionPresentation } from './attributionPresentation';

const valueOrDash = (value?: string | number) => value || '-';

const getFirstAvailableSignalTab = (
  c?: Contact | null,
  fallback: 'website' | 'market' | 'linkedin' = 'website'
): 'website' | 'market' | 'linkedin' => {
  const byType = c?.signals?.by_type;
  if (!byType) return fallback;
  if ((byType.website ?? 0) > 0) return 'website';
  if ((byType.market ?? 0) > 0) return 'market';
  if ((byType.linkedin ?? 0) > 0) return 'linkedin';
  return fallback;
};

const ContactDetailsDrawerSkeleton = () => (
  <div className="flex h-full min-h-0 w-full flex-col">
    <div className={cn(styles.container, 'border-b border-border p-5')}>
      <div className={cn(styles.header, 'flex items-center justify-between gap-6 pr-9')}>
        <div className={cn(styles.identity, 'flex min-w-0 flex-[4] items-center gap-3')}>
          <Skeleton className="size-[54px] shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-40 rounded" />
              <Skeleton className="h-4 w-4 rounded" />
            </div>
            <Skeleton className="h-4 w-56 rounded" />
            <div className="flex items-center gap-1">
              <Skeleton className="size-4 rounded-md" />
              <Skeleton className="h-4 w-32 rounded" />
            </div>
          </div>
        </div>
        <div
          className={cn(
            styles.qualifications,
            'flex min-w-0 flex-[3] flex-col gap-2 border-l border-border pl-6'
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Skeleton className="h-4 w-16 rounded" />
            <Skeleton className="h-6 w-28 rounded-full" />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <Skeleton className="h-4 w-16 rounded" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        </div>
      </div>
      <div
        className={cn(
          styles.contactMethods,
          'mt-5 flex h-8 w-full min-w-0 items-center gap-4 rounded-[10px] bg-muted px-6'
        )}
      >
        <Skeleton className="h-4 w-28 rounded" />
        <Skeleton className="h-4 w-44 rounded" />
        <Skeleton className="h-4 w-32 rounded" />
      </div>
    </div>

    <div className="h-11 border-b border-border px-2">
      <div className="flex h-11 items-center gap-5">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-5 w-28 rounded" />
        ))}
      </div>
    </div>

    <div className="min-h-0 flex-1 overflow-hidden">
      <div className="px-5 py-3">
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className={cn('flex flex-col gap-2', index === 2 && 'col-span-2')}>
              <Skeleton className="h-3 w-24 rounded" />
              <Skeleton className="h-5 rounded" width={index % 2 === 0 ? 180 : 140} />
            </div>
          ))}
        </div>
      </div>
      {Array.from({ length: 2 }).map((_, sectionIndex) => (
        <div key={sectionIndex} className="border-t border-border px-5 py-3">
          <Skeleton className="mb-4 h-3 w-28 rounded" />
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex flex-col gap-2">
                <Skeleton className="h-3 w-20 rounded" />
                <Skeleton className="h-5 w-36 rounded" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export type ContactDrawerTab = 'person' | 'company' | 'signals' | 'target-lists';

function resolveActiveTab(
  tab: 'details' | 'signals' | 'company' | 'person' | 'target-lists',
  allowed?: ContactDrawerTab[]
): ContactDrawerTab {
  const normalized: ContactDrawerTab =
    tab === 'signals'
      ? 'signals'
      : tab === 'company'
        ? 'company'
        : tab === 'target-lists'
          ? 'target-lists'
          : 'person';
  if (!allowed || allowed.length === 0) return normalized;
  if (allowed.includes(normalized)) return normalized;
  if (allowed.includes('signals')) return 'signals';
  return allowed[0];
}

export default function ContactDetailsDrawer({
  contactId,
  initialTab,
  visibleTabs,
  initialSignalTab,
  onSignalTabChange,
  isOpen,
  onOpenChange,
  embedded = false,
  readOnly = false,
  clientId: propClientId,
}: {
  contactId: string;
  initialTab: 'details' | 'signals' | 'company' | 'person' | 'target-lists';
  visibleTabs?: ContactDrawerTab[];
  initialSignalTab?: 'website' | 'market' | 'linkedin';
  onSignalTabChange?: (signalTab: 'website' | 'market' | 'linkedin') => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  embedded?: boolean;
  readOnly?: boolean;
  clientId?: string;
}) {
  const t = useTranslations('contactDrawer');
  const tSignals = useTranslations('signals');
  const { trackEvent } = useIntercom();
  const [activeTab, setActiveTab] = useState<ContactDrawerTab>(() =>
    resolveActiveTab(initialTab, visibleTabs)
  );
  const [activeSignalTab, setActiveSignalTab] = useState<'website' | 'market' | 'linkedin'>(
    initialSignalTab || 'website'
  );
  const [isPhoneCopied, setIsPhoneCopied] = useState(false);
  const phoneCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isEmailCopied, setIsEmailCopied] = useState(false);
  const emailCopyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current && contactId) {
      trackEvent('buyer_card_open', {
        current_path: window.location.pathname,
        contact_id: contactId,
      });
    }
    previousIsOpenRef.current = isOpen;
  }, [contactId, isOpen, trackEvent]);

  useEffect(() => {
    setActiveTab(resolveActiveTab(initialTab, visibleTabs));
    setActiveSignalTab(initialSignalTab || 'website');
  }, [contactId, initialSignalTab, initialTab, isOpen, visibleTabs]);

  useEffect(() => {
    return () => {
      if (emailCopyTimeoutRef.current) clearTimeout(emailCopyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setIsPhoneCopied(false);
    return () => {
      if (phoneCopyTimeoutRef.current) clearTimeout(phoneCopyTimeoutRef.current);
    };
  }, [contactId, isOpen]);

  const salesforceEmbed = useSalesforceEmbed();
  const params = useParams<{ clientId?: string }>();
  const clientId = propClientId || salesforceEmbed.clientId || params.clientId;
  const isEmbedReadOnly = readOnly || salesforceEmbed.isEmbed;

  const queryClient = useQueryClient();
  const {
    data: contact,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['contact-details-drawer', clientId, contactId, salesforceEmbed.isEmbed],
    queryFn: () => {
      if (salesforceEmbed.isEmbed && salesforceEmbed.token && clientId) {
        return getSalesforceContact(clientId, contactId, salesforceEmbed.token);
      }
      return getContact(clientId!, contactId);
    },
    enabled: isOpen && !!clientId && !!contactId,
    refetchOnMount: 'always',
  });

  const hasAutoSelectedSignalRef = useRef(false);

  useEffect(() => {
    hasAutoSelectedSignalRef.current = false;
  }, [contactId, isOpen]);

  useEffect(() => {
    if (
      contact &&
      activeTab === 'signals' &&
      !initialSignalTab &&
      !hasAutoSelectedSignalRef.current
    ) {
      hasAutoSelectedSignalRef.current = true;
      const firstTab = getFirstAvailableSignalTab(contact, 'website');
      setActiveSignalTab(firstTab);
      onSignalTabChange?.(firstTab);
    }
  }, [contact, activeTab, initialSignalTab, onSignalTabChange]);

  const contactInfo = contact?.contact_info;
  const companyInfo = contact?.company_info;
  const contactName = contactInfo
    ? [contactInfo.name?.first, contactInfo.name?.middle, contactInfo.name?.last]
        .filter(Boolean)
        .join(' ')
    : '';
  const companyName = companyInfo?.name || companyInfo?.full_name || '';
  const email = contactInfo?.email?.work;
  const phone = contactInfo?.phone?.work || contactInfo?.phone?.mobile;
  const buyerAttribution = getAttributionPresentation(contact?.attribution);

  const copyPhone = async () => {
    if (!phone) return;
    try {
      await navigator.clipboard.writeText(phone);
      setIsPhoneCopied(true);
      if (phoneCopyTimeoutRef.current) clearTimeout(phoneCopyTimeoutRef.current);
      phoneCopyTimeoutRef.current = setTimeout(() => setIsPhoneCopied(false), 2000);
      trackEvent('buyer_card_phone_copy', {
        current_path: window.location.pathname,
        contact_id: contactId,
      });
    } catch (error) {
      console.error('Failed to copy phone:', error);
    }
  };

  const copyEmail = async () => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setIsEmailCopied(true);
      if (emailCopyTimeoutRef.current) clearTimeout(emailCopyTimeoutRef.current);
      emailCopyTimeoutRef.current = setTimeout(() => setIsEmailCopied(false), 2000);
      trackEvent('buyer_card_email_copy', {
        current_path: window.location.pathname,
        contact_id: contactId,
      });
    } catch (error) {
      console.error('Failed to copy email:', error);
    }
  };

  const { mutateAsync: updateContactMutation } = useMutation({
    mutationFn: (payload: Parameters<typeof updateContact>[2]) =>
      updateContact(clientId!, contactId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['contact-details-drawer', clientId, contactId],
      });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const onInlineEdit = async (key: string, value: unknown) => {
    if (isEmbedReadOnly) throw new Error('Editing is disabled in embedded views.');
    if (!contact) throw new Error(t('buyerUnavailable'));
    const [rootKey, ...path] = key.split('.');
    if (rootKey !== 'contact_info' && rootKey !== 'company_info') {
      throw new Error(t('unsupportedContactField'));
    }
    const normalizedValue = key.endsWith('postal_code') ? value?.toString() : value;
    const nextValue =
      path[0] === 'location' && path[1]
        ? { location: { [path[1]]: normalizedValue } }
        : path.length
          ? objectPathSet(path.join('.'), normalizedValue, {})
          : normalizedValue;
    const payload =
      rootKey === 'company_info'
        ? {
            company_id: companyInfo?.company_id,
            company_info: {
              ...(nextValue as Record<string, unknown>),
            },
          }
        : { contact_info: nextValue };
    await updateContactMutation(payload as Parameters<typeof updateContact>[2]);
    return t('buyerUpdated');
  };

  const item = (name: string, value: InfoTableItem['value'], columnSpan?: 1 | 2, key?: string) => ({
    name,
    value: key ? value : valueOrDash(value as string | number),
    columnSpan,
    key: isEmbedReadOnly ? undefined : key,
    isInlineEdit: !isEmbedReadOnly && !!key,
  });

  const section = (title: string, data: InfoTableItem[]) => (
    <div className="border-t border-border px-5 py-3 first:border-t-0">
      <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{title}</h3>
      <InfoTable
        data={data}
        columns={2}
        onInlineEdit={isEmbedReadOnly ? undefined : onInlineEdit}
      />
    </div>
  );

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open && contactId) {
      trackEvent('buyer_card_close', {
        current_path: window.location.pathname,
        contact_id: contactId,
      });
    }
    onOpenChange(open);
  };

  return (
    <Drawer
      embedded={embedded}
      modal={false}
      showOverlay={!embedded}
      closeOnClickOutside={!embedded}
      preventOpenAutoFocus={false}
      isOpen={isOpen}
      onOpenChange={handleDrawerOpenChange}
      bodyClassName="p-0"
      bodyStyle={embedded ? undefined : { width: '50vw', minWidth: 320, maxWidth: '50vw' }}
      showClose={!embedded}
      scrollClassName="p-0"
      scrollBody={false}
    >
      {isLoading ? (
        <ContactDetailsDrawerSkeleton />
      ) : isError || !contact ? (
        <div className="flex h-full min-h-48 w-full flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="font-medium text-foreground">Unable to load contact details</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Your access may have expired. Close and reopen the contact, or try again.
          </p>
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            onClick={() => void refetch()}
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="flex h-full min-h-0 w-full flex-col">
          <div className={cn(styles.container, 'border-b border-border p-5')}>
            <div className={cn(styles.header, 'flex items-center justify-between gap-6 pr-9')}>
              <div className={cn(styles.identity, 'min-w-0 flex-[4]')}>
                <ContactMiniCard
                  clickable={false}
                  wrapperClassName="min-w-0"
                  avatarSize={54}
                  contactNameClass="text-base"
                  contactTitleClass="text-sm"
                  onLinkedinClick={() => {
                    trackEvent('buyer_card_linkedin_click', {
                      current_path: window.location.pathname,
                      contact_id: contactId,
                    });
                  }}
                  footer={
                    companyName ? (
                      <CompanyMiniCard
                        avatarSize={16}
                        avatarImageSize={16}
                        wrapperClassName="min-w-0 shrink gap-1"
                        company={{
                          id: companyInfo?.company_id || '',
                          name: companyName,
                          logo: companyInfo?.logo || '',
                        }}
                      />
                    ) : null
                  }
                  contact={{
                    id: contact.id,
                    name: contactName,
                    linkedin: contactInfo?.social?.linkedin,
                    title: contactInfo?.role?.title,
                    profile_pic: contactInfo?.photo_url || '',
                  }}
                />
              </div>

              {(contact.eval?.persona_name || contact.eval?.icp_name) && (
                <div
                  className={cn(
                    styles.qualifications,
                    'flex min-w-0 flex-[3] flex-col gap-2 border-l border-border pl-6'
                  )}
                >
                  {contact.eval?.persona_name && (
                    <div
                      className={cn(
                        styles.qualification,
                        'flex min-w-0 items-center gap-2 text-sm text-muted-foreground'
                      )}
                    >
                      <div
                        className={cn(
                          styles.qualificationLabel,
                          'flex w-16 shrink-0 items-center gap-1'
                        )}
                      >
                        <Icon name="check" size={3} className="text-success-base" />
                        <span className="text-[11px]">{t('persona')}</span>
                      </div>
                      <div className="min-w-0 max-w-full flex-1">
                        <QualificationPill
                          type="persona"
                          label={contact.eval.persona_name}
                          reason={contact.eval.persona_reasoning}
                          className="uppercase"
                          tooltipSide="left"
                        />
                      </div>
                    </div>
                  )}
                  {contact.eval?.icp_name && (
                    <div
                      className={cn(
                        styles.qualification,
                        'flex min-w-0 items-center gap-2 text-sm text-muted-foreground'
                      )}
                    >
                      <div
                        className={cn(
                          styles.qualificationLabel,
                          'flex w-16 shrink-0 items-center gap-1'
                        )}
                      >
                        <Icon name="check" size={3} className="text-success-base" />
                        <span className="text-[11px]">{t('icp')}</span>
                      </div>
                      <div className="min-w-0 max-w-full flex-1">
                        <QualificationPill
                          type="icp"
                          label={contact.eval.icp_name}
                          reason={contact.eval.icp_reasoning}
                          className="uppercase"
                          tooltipSide="left"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {(email || phone || buyerAttribution?.sourceName) && (
              <div
                className={cn(
                  styles.contactMethods,
                  'mt-5 flex h-8 w-full min-w-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-[10px] bg-muted px-6 text-xs text-foreground'
                )}
              >
                {buyerAttribution?.sourceName && (
                  <div className={cn(styles.contactMethod, 'flex min-w-0 items-center gap-2')}>
                    <span className="text-muted-foreground text-xs">{t('source')}</span>
                    {buyerAttribution.sourceLogo && (
                      <img
                        src={buyerAttribution.sourceLogo}
                        alt=""
                        className="size-4 object-contain"
                        onError={e => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    <span className="truncate text-xs font-medium text-foreground">
                      {buyerAttribution.sourceName}
                    </span>
                  </div>
                )}
                {email && (
                  <div
                    className={cn(
                      styles.contactMethod,
                      'flex min-w-0 items-center gap-2 border-l border-border pl-4 first:border-l-0 first:pl-0'
                    )}
                  >
                    <Mail size={16} className="shrink-0 text-muted-foreground" />
                    <span className="truncate text-xs font-medium text-foreground">{email}</span>
                    <button
                      type="button"
                      className="flex shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                      aria-label={isEmailCopied ? t('emailCopied') : t('copyEmail')}
                      onClick={() => void copyEmail()}
                    >
                      {isEmailCopied ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                )}
                {phone && (
                  <div
                    className={cn(
                      styles.contactMethod,
                      'flex min-w-0 items-center gap-2 border-l border-border pl-4 first:border-l-0 first:pl-0'
                    )}
                  >
                    <PhoneCall size={16} className="shrink-0 text-muted-foreground" />
                    <span className="truncate text-xs font-medium text-foreground">{phone}</span>
                    <button
                      type="button"
                      className="flex shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                      aria-label={isPhoneCopied ? t('phoneCopied') : t('copyPhone')}
                      onClick={() => void copyPhone()}
                    >
                      {isPhoneCopied ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-b border-border">
            <CustomScroll orientation="horizontal" type="hover" viewportClassName="px-5 pb-0">
              <Tabs
                value={activeTab}
                onValueChange={value => {
                  const nextTab = value as typeof activeTab;
                  setActiveTab(nextTab);
                  if (nextTab === 'signals') {
                    const firstTab = getFirstAvailableSignalTab(contact, 'website');
                    setActiveSignalTab(firstTab);
                    onSignalTabChange?.(firstTab);
                  }
                  const tabEvents: Record<string, string> = {
                    person: 'buyer_card_person_info',
                    company: 'buyer_card_company_info',
                    signals: 'buyer_card_signals',
                    'target-lists': 'buyer_card_target_lists',
                  };
                  if (tabEvents[value]) {
                    trackEvent(tabEvents[value], {
                      current_path: window.location.pathname,
                      contact_id: contactId,
                    });
                  }
                }}
                className="h-11 min-w-full w-max"
                variant="bordered"
              >
                <TabsList className="h-11 min-w-full w-max justify-start rounded-none bg-transparent p-0">
                  {[
                    { value: 'person' as const, label: t('tabs.personInfo'), icon: User },
                    { value: 'company' as const, label: t('tabs.companyInfo'), icon: Building },
                    {
                      value: 'signals' as const,
                      label: t('tabs.signals', { count: contact.signals?.total ?? 0 }),
                      icon: Radio,
                    },
                    {
                      value: 'target-lists' as const,
                      label: t('tabs.targetLists'),
                      icon: ListCheck,
                    },
                  ]
                    .filter(
                      tab =>
                        !visibleTabs || visibleTabs.length === 0 || visibleTabs.includes(tab.value)
                    )
                    .map(tab => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className="h-full flex-1 gap-1.5 rounded-none text-sm px-2"
                      >
                        <tab.icon size={16} />
                        {tab.label}
                      </TabsTrigger>
                    ))}
                </TabsList>
              </Tabs>
            </CustomScroll>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {activeTab === 'signals' ? (
              <div className="flex h-full min-h-0 flex-col">
                <div className="shrink-0 border-b border-border px-5 py-3">
                  <Tabs
                    variant="block"
                    value={activeSignalTab}
                    onValueChange={value => {
                      const signalTab = value as 'website' | 'market' | 'linkedin';
                      setActiveSignalTab(signalTab);
                      onSignalTabChange?.(signalTab);
                      trackEvent(`buyer_card_signals_${signalTab}`, {
                        current_path: window.location.pathname,
                        contact_id: contactId,
                      });
                    }}
                  >
                    <TabsList>
                      <TabsTrigger value="website">
                        <Globe size={16} />
                        {tSignals('tabs.websiteSignals')}
                      </TabsTrigger>
                      <TabsTrigger value="market">
                        <Megaphone size={16} />
                        {tSignals('tabs.marketSignals')}
                      </TabsTrigger>
                      <TabsTrigger value="linkedin">
                        <Linkedin size={16} />
                        {tSignals('tabs.linkedinSignals')}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <div className="min-h-0 flex-1">
                  <SignalDetails
                    key={activeSignalTab}
                    onFilterOpenChange={open => {
                      if (open) {
                        trackEvent('buyer_card_signals_filter_open', {
                          current_path: window.location.pathname,
                          contact_id: contactId,
                        });
                      }
                    }}
                    visitor={
                      {
                        id: contact.id,
                        client_id: contact.client_id,
                        company_id: companyInfo?.company_id || '',
                        total_signals: contact.signals?.total || 0,
                      } as SignalContactSummary
                    }
                    detailType="contact"
                    signalType={activeSignalTab}
                    showContactOpenButton={false}
                    hideContactHeader
                    hideFilters={embedded}
                    contactHeader={{
                      id: contact.id,
                      name: contactName,
                      linkedin: contactInfo?.social?.linkedin,
                      title: contactInfo?.role?.title,
                      profile_pic: contactInfo?.photo_url || '',
                    }}
                    companyHeader={
                      companyInfo
                        ? {
                            id: companyInfo.company_id || '',
                            name: companyName,
                            logo: companyInfo.logo || '',
                            linkedin: companyInfo.social?.linkedin,
                          }
                        : null
                    }
                  />
                </div>
              </div>
            ) : (
              <CustomScroll className="h-full" orientation="vertical" viewportClassName="p-0">
                {activeTab === 'person' ? (
                  <div>
                    <div className="px-5 py-3">
                      <InfoTable
                        columns={2}
                        onInlineEdit={onInlineEdit}
                        data={[
                          item(
                            t('person.firstName'),
                            contactInfo?.name?.first,
                            1,
                            'contact_info.name.first'
                          ),
                          item(
                            t('person.lastName'),
                            contactInfo?.name?.last,
                            1,
                            'contact_info.name.last'
                          ),
                          item(
                            t('person.role'),
                            contactInfo?.role?.title,
                            2,
                            'contact_info.role.title'
                          ),
                          ...(buyerAttribution?.sourceName
                            ? [item(t('person.attribution'), buyerAttribution.sourceName, 2)]
                            : []),
                          item(t('person.createdAt'), formatDate(contact.created_at, true)),
                          item(t('person.updatedAt'), formatDate(contact.updated_at, true)),
                        ]}
                      />
                    </div>
                    {section(t('sections.address'), [
                      item(
                        t('person.city'),
                        contactInfo?.location?.city,
                        1,
                        'contact_info.location.city'
                      ),
                      item(
                        t('person.state'),
                        contactInfo?.location?.state,
                        1,
                        'contact_info.location.state'
                      ),
                      item(
                        t('person.country'),
                        contactInfo?.location?.country,
                        1,
                        'contact_info.location.country'
                      ),
                      item(
                        t('person.postalCode'),
                        contactInfo?.location?.postal_code,
                        1,
                        'contact_info.location.postal_code'
                      ),
                    ])}
                    {section(t('sections.buyerAndSocial'), [
                      item(
                        t('person.phone'),
                        contactInfo?.phone?.work || contactInfo?.phone?.mobile,
                        1,
                        'contact_info.phone.work'
                      ),
                      item(
                        t('person.email'),
                        contactInfo?.email?.work,
                        1,
                        'contact_info.email.work'
                      ),
                      item(
                        t('person.linkedin'),
                        contactInfo?.social?.linkedin,
                        2,
                        'contact_info.social.linkedin'
                      ),
                      item(
                        t('person.twitter'),
                        contactInfo?.social?.twitter,
                        2,
                        'contact_info.social.twitter'
                      ),
                      item(
                        t('person.facebook'),
                        contactInfo?.social?.facebook,
                        2,
                        'contact_info.social.facebook'
                      ),
                      item(
                        t('person.github'),
                        contactInfo?.social?.github,
                        2,
                        'contact_info.social.github'
                      ),
                    ])}
                  </div>
                ) : activeTab === 'company' ? (
                  <div>
                    <div className="px-5 py-3">
                      <InfoTable
                        columns={2}
                        onInlineEdit={onInlineEdit}
                        data={[
                          item(
                            t('company.companyName'),
                            companyInfo?.full_name,
                            1,
                            'company_info.full_name'
                          ),
                          item(t('company.shortName'), companyInfo?.name, 1, 'company_info.name'),
                          item(
                            t('company.website'),
                            companyInfo?.website,
                            2,
                            'company_info.website'
                          ),
                          {
                            ...item(
                              t('company.description'),
                              companyInfo?.description,
                              2,
                              'company_info.description'
                            ),
                            type: 'textarea',
                          },
                          {
                            ...item(
                              t('company.industry'),
                              Array.isArray(companyInfo?.industries) ? companyInfo.industries : [],
                              2,
                              'company_info.industries'
                            ),
                            type: 'tags',
                          },
                          {
                            ...item(
                              t('company.technologies'),
                              Array.isArray(companyInfo?.technology_names)
                                ? companyInfo.technology_names
                                : [],
                              2,
                              'company_info.technology_names'
                            ),
                            type: 'tags',
                          },
                          {
                            ...item(
                              t('company.foundedYear'),
                              companyInfo?.founded_year,
                              1,
                              'company_info.founded_year'
                            ),
                            type: 'number',
                            shouldFormat: false,
                          },
                          {
                            ...item(
                              t('company.revenue'),
                              companyInfo?.annual_revenue,
                              1,
                              'company_info.annual_revenue'
                            ),
                            type: 'number',
                          },
                          {
                            ...item(
                              t('company.employeeCount'),
                              companyInfo?.employee_count,
                              1,
                              'company_info.employee_count'
                            ),
                            type: 'number',
                          },
                          item(t('company.createdAt'), formatDate(contact.created_at, true)),
                          item(t('company.updatedAt'), formatDate(contact.updated_at, true)),
                        ]}
                      />
                    </div>
                    {section(t('sections.address'), [
                      item(
                        t('company.city'),
                        companyInfo?.location?.city,
                        1,
                        'company_info.location.city'
                      ),
                      item(
                        t('company.state'),
                        companyInfo?.location?.state,
                        1,
                        'company_info.location.state'
                      ),
                      item(
                        t('company.country'),
                        companyInfo?.location?.country,
                        1,
                        'company_info.location.country'
                      ),
                      item(
                        t('company.postalCode'),
                        companyInfo?.location?.postal_code,
                        1,
                        'company_info.location.postal_code'
                      ),
                    ])}
                    {section(t('sections.buyerAndSocial'), [
                      item(
                        t('company.phone'),
                        companyInfo?.phone?.main,
                        1,
                        'company_info.phone.main'
                      ),
                      item(t('company.email'), companyInfo?.email, 1, 'company_info.email'),
                      item(
                        t('company.linkedin'),
                        companyInfo?.social?.linkedin,
                        2,
                        'company_info.social.linkedin'
                      ),
                      item(
                        t('company.twitter'),
                        companyInfo?.social?.twitter,
                        2,
                        'company_info.social.twitter'
                      ),
                      item(
                        t('company.facebook'),
                        companyInfo?.social?.facebook,
                        2,
                        'company_info.social.facebook'
                      ),
                    ])}
                  </div>
                ) : (
                  <div className="min-h-64">
                    <ContactTargetList contact={contact} />
                  </div>
                )}
              </CustomScroll>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
